<template>
  <div>
    <v-container
      fluid
      fill-height
      class="no-pad"
    >
      <v-layout
        align-space-around
        justify-center
        column
        fill-height
      >
        <v-flex class="map-container">
          <prime-google-map
            :map-center="mapCenter"
            :pipes-a="poiPipes"
            :pipes-b="mapPipes"
            :loggers="loggers"
            :markers="markers"
            :nodes="mapFeatures"
            :active-pipes="activePipes"
            :oobs="oobMarkers"
            @marker-click="onMarkerClick"
            @map-zoom_changed="onMapZoomChanged"
          />
          <v-btn
            color="primary"
            dark
            absolute
            bottom
            left
            fab
            class="custom"
            @click="sheet = true"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            v-if="!loaded"
            color="primary"
            dark
            absolute
            bottom
            middle
            fab
            class="custom"
            @click="onNetworkLoad"
          >
            {{ strings.load }}
            <v-progress-circular
              v-if="loading"
              :size="10"
              color="secondary"
              indeterminate
            />
          </v-btn>
        </v-flex>
      </v-layout>
      <v-bottom-sheet v-model="sheet">
        <v-list
          dark
          class="primeweb darken-3"
          :class="{ paused: paused, 'on-site': isOnSite }"
        >
          <v-list-tile @click="onDataButton(correlationData.id)">
            <v-list-tile-avatar>
              <v-avatar
                size="42px"
                color="primary"
              >
                <v-icon color="text">
                  mdi-chart-line
                </v-icon>
              </v-avatar>
            </v-list-tile-avatar>
            <v-list-tile-title>{{ strings.detailsButton }}</v-list-tile-title>
          </v-list-tile>
          <v-list-tile @click="onPauseButton">
            <v-list-tile-avatar>
              <v-avatar
                size="42px"
                color="primary"
              >
                <v-icon
                  v-if="paused"
                  color="text"
                >
                  mdi-play
                </v-icon>
                <v-icon
                  v-else
                  color="text"
                >
                  mdi-pause
                </v-icon>
              </v-avatar>
            </v-list-tile-avatar>
            <v-list-tile-title v-if="paused">
              {{ strings.activate }}
            </v-list-tile-title>
            <v-list-tile-title v-else>
              {{ strings.pause }}
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile @click="onOnSiteLeaveSiteButton">
            <v-list-tile-avatar>
              <v-avatar
                size="42px"
                color="primary"
              >
                <v-icon color="text">
                  mdi-map-marker
                </v-icon>
              </v-avatar>
            </v-list-tile-avatar>
            <v-list-tile-title v-if="!isOnSite">
             {{ strings.onSite }}
            </v-list-tile-title>
            <v-list-tile-title v-else>
              {{ strings.leaveSite }}
            </v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-bottom-sheet>
    </v-container>
  </div>
</template>

<script>
import { PageEventHandlers } from "../mixins/pageEventHandlers";
import { mapGetters } from "vuex";
import MarkerData from "../models/MarkerData";
import { Logger, Pipe, GenericMarkerData } from "@primayer/prime-google-map";

export default {
  name: "MapDetail",
  mixins: [PageEventHandlers],
  data() {
    return {
      sheet: false,
      activePdpId: null,
      //  poi: null,
      showLocationDialog: false,
      loading: false,
      zoom: 15,
    };
  },
  computed: {
    ...mapGetters("page", ["contentUrl", "strings"]),
    ...mapGetters("poi", ["activeGroupPoi"]),
    ...mapGetters("network", ["pipes", "hydrants", "washouts", "valves", "prvs", "pmvs", "meters", "tappingPoints"]),
    mapCenter() {
      if (this.poiPipes.length > 0) {
        const loc = this.poiPipes[0].startLatLong;
        return loc;
      }
      return null;
    },
    poi() {
      return this.activeGroupPoi;
    },
    correlationData() {
      return this.activeGroupPoi != null ? this.activeGroupPoi : null;
    },
    poiPipes() {
      if (this.correlationData && this.correlationData.pois) {
        let rawPipes = [];
        this.correlationData.pois.forEach(pdp => {
          let pdpPipes = pdp.pipes;
          rawPipes = [...rawPipes, ...pdpPipes];
        });
        rawPipes = [...new Set(rawPipes)];

        let pPipes = [];
        rawPipes.forEach(rawPipe => {
          pPipes.push(new Pipe(rawPipe));
        });

        return pPipes;
      } else {
        return [];
      }
    },
    pois() {
      return this.correlationData != null && this.correlationData.pois != null
        ? this.correlationData.pois
        : [];
    },
    activePipes() {
      if (this.activePdpId) {
        let matchingPDPArray = this.poi.pois.filter(poi => {
          return poi.id === this.activePdpId;
        });
        let rawPipes = matchingPDPArray[0].pipes;
        let pipes = [];
        rawPipes.forEach(rawPipe => {
          pipes.push(new Pipe(rawPipe));
        });

        return pipes;
      }
      return [];
    },
    loggers() {
      if (this.pois.length > 0) {
        let loggerIdArray = [];
        let allLoggers = [];
        this.pois.forEach(pdp => {
          let idExisits =
            loggerIdArray.findIndex(element => {
              return element === pdp.leftLogger.loggerName;
            }) >= 0;
          if (!idExisits) {
            if (pdp.leftLogger) {
              loggerIdArray.push(pdp.leftLogger.loggerName);
              const logger = {
                ...pdp.leftLogger,
                name: pdp.leftLogger.loggerName,
                id: pdp.leftLogger.loggerId,
                coords: {
                  latitude: pdp.leftLogger.latitude,
                  longitude: pdp.leftLogger.longitude
                },
                icon: {
                  url: `${this.contentUrl}/icons/${pdp.leftLogger.icon}`,
                  size: null,
                  scaledSize: null,
                  origin: [0, 0],
                  anchor: [15, 35],
                  labelOrigin: [21, -5]
                },
                label: {
                  text: pdp.leftLogger.loggerName,
                  color: "black",
                  fontSize: "11px",
                  fontWeight: "bold"
                }
              };
              allLoggers.push(new GenericMarkerData(logger));
            }
          }
          idExisits =
            loggerIdArray.findIndex(element => {
              return element === pdp.rightLogger.loggerName;
            }) >= 0;
          if (!idExisits) {
            if (pdp.rightLogger) {
              loggerIdArray.push(pdp.rightLogger.loggerName);
              const logger = {
                ...pdp.rightLogger,
                name: pdp.rightLogger.loggerName,
                id: pdp.rightLogger.loggerId,
                coords: {
                  latitude: pdp.rightLogger.latitude,
                  longitude: pdp.rightLogger.longitude
                },
                icon: {
                  url: `${this.contentUrl}/icons/${pdp.leftLogger.icon}`,
                  size: null,
                  scaledSize: null,
                  origin: [0, 0],
                  anchor: [15, 35],
                  labelOrigin: [21, -5]
                },
                label: {
                  text: pdp.rightLogger.loggerName,
                  color: "black",
                  fontSize: "11px",
                  fontWeight: "bold"
                }
              };
              allLoggers.push(new GenericMarkerData(logger));
            }
          }
        });
        return allLoggers;
      } else {
        return [];
      }
    },
    markers() {
      if (this.poi && this.poi.pois) {
        var leakArr = [];
        this.poi.pois.forEach(poi => {
          var model = new MarkerData({
            id: poi.id,
            name: "correlation",
            latitude: poi.latitude,
            longitude: poi.longitude,
            opacity: 1,
            zIndex: 2,
            icon: {
              url: `${this.contentUrl}/icons/LeakMap2.png`,
              size: null,
              scaledSize: null,
              origin: [0, 0],
              anchor: [12, 12]
            }
          });
          leakArr.push(model);
        });
        return leakArr;
      } else {
        return [];
      }
    },
    oobMarkers() {
      var arr = [];
      if (this.poi && this.poi.oobMarkers) {
        this.poi.oobMarkers.forEach(oob => {
          var model = new MarkerData({
            id: oob.id,
            name: "oob",
            latitude: oob.latitude,
            longitude: oob.longitude,
            opacity: 1,
            zIndex: 5,
            icon: {
              url: `${this.contentUrl}/icons/arrow-${oob.name}.png`,
              size: null,
              scaledSize: [16, 16],
              origin: [0, 0],
              anchor: [8, 8],
            },
          });
          model.oobItem = oob;
          arr.push(model);
        })
      }
      return arr;
    },
    mapPipes() {
      var arr = [];
      this.pipes.forEach(pipe => {
        arr.push(new Pipe(pipe));
      })
      return arr;
    },
    mapFeatures() {
      var arr = [];
      this.hydrants.forEach(hydrant => {
        var model = new GenericMarkerData({
          id: hydrant.id,
          name: hydrant.name,
          latitude: hydrant.latitude,
          longitude: hydrant.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: hydrant.name,
          icon: {
            url: "./img/hydrant.png",
            size: [16, 16],
            scaledSize: [16, 16],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });
    
      this.valves.forEach(valve => {
        var model = new GenericMarkerData({
          id: valve.id,
          name: valve.name,
          latitude: valve.latitude,
          longitude: valve.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: valve.name,
          icon: {
            url: "./img/valve.png",
            size: [20, 15],
            scaledSize: [20, 15],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });

      this.washouts.forEach(washout => {
        var model = new GenericMarkerData({
          id: washout.id,
          name: washout.name,
          latitude: washout.latitude,
          longitude: washout.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: washout.name,
          icon: {
            url: "./img/washout.png",
            size: [16, 16],
            scaledSize: [16, 16],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });

      this.prvs.forEach(prv => {
        var model = new GenericMarkerData({
          id: prv.id,
          name: prv.name,
          latitude: prv.latitude,
          longitude: prv.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: prv.name,
          icon: {
            url: "./img/prv.png",
            size: [16, 16],
            scaledSize: [16, 16],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });
    
      this.pmvs.forEach(pmv => {
        var model = new GenericMarkerData({
          id: pmv.id,
          name: pmv.name,
          latitude: pmv.latitude,
          longitude: pmv.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: pmv.name,
          icon: {
            url: "./img/pmv.png",
            size: [16, 16],
            scaledSize: [16, 16],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });
    
      this.meters.forEach(meter => {
        var model = new GenericMarkerData({
          id: meter.id,
          name: meter.name,
          latitude: meter.latitude,
          longitude: meter.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: meter.name,
          icon: {
            url: "./img/districtMeter.png",
            size: [20, 15],
            scaledSize: [20, 15],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });
    
      this.tappingPoints.forEach(tp => {
        var model = new GenericMarkerData({
          id: tp.id,
          name: tp.name,
          latitude: tp.latitude,
          longitude: tp.longitude,
          opacity: 1,
          zIndex: 2,
          draggable: false,
          title: tp.name,
          icon: {
            url: "./img/tapping.png",
            size: [16, 16],
            scaledSize: [16, 16],
            origin: [0, 0],
            anchor: [10, 8],
          }
        });
        arr.push(model);
      });
      
      return arr;
    },
    loaded() {
      return this.mapPipes.length > 0;
    }
  },
  methods: {
    onNetworkLoad() {
      if(!this.loading) {
        this.loading = true;
        this.$store.dispatch("network/getNetwork")
        .then(() => {
          this.loading = false;
        })
      }
    },
    onMarkerClick(event, marker) {
      this.activePdpId = marker.id;
    },
    onMapZoomChanged(event) {
      this.zoom = event;
    },
    onOnSiteLeaveSiteButton() {
      if (!this.isOnSite) {
        this.onSiteButtonClicked();
      } else {
        this.onLeaveSiteButton();
      }
    }
  }
};
</script>

<style scoped>
.map-container {
  height: 93vh;
  width: 100%;
}
.container.fill-height.no-pad {
  width: 100vw;
  padding: 0;
  margin: 0;
}

.v-btn--bottom.v-btn--absolute.v-btn--small.custom {
  bottom: 4vh;
  color: #333 !important;
}

.v-btn--bottom.v-btn--absolute.custom {
  bottom: 4vh;
  color: #333 !important;
  font-weight: bold;
}
</style>
